
import { useEffect, useState } from "react";
import { Bell, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { toast } from "sonner";

// The Notification type now matches the DB row (read state is persisted)
type Notification = {
  id: string;
  message: string;
  timestamp: string;
  isRead: boolean;
};

export const NotificationDropdown = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [userId, setUserId] = useState<string | null>(null);

  // Fetch notifications from Supabase
  const loadNotifications = async (userId: string) => {
    const { data, error } = await supabase
      .from("user_notifications")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(25);

    if (error) {
      console.error("Failed to fetch notifications", error);
      return;
    }

    const notifs = (data || []).map((n) => ({
      id: n.id,
      message: n.message,
      timestamp: n.created_at,
      isRead: n.is_read,
    }));

    setNotifications(notifs);
    setUnreadCount(notifs.filter((n) => !n.isRead).length);
  };

  // Listen for realtime inserts (new notifications)
  // and refresh on changes
  useEffect(() => {
    let channel: any;

    const setup = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      setUserId(user.id);

      // Initial fetch
      loadNotifications(user.id);

      // Realtime: subscribe to this user's new notifications
      channel = supabase
        .channel('user-notifications')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'user_notifications',
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            if (payload.eventType === 'INSERT') {
              const notif = payload.new;
              toast("New Notification", { description: notif.message });
              loadNotifications(user.id); // refresh notifications
            }
            if (payload.eventType === 'UPDATE' || payload.eventType === 'DELETE') {
              loadNotifications(user.id); // refresh on changes
            }
          }
        )
        .subscribe();
    };

    setup();

    return () => {
      if (channel) supabase.removeChannel(channel);
    };
    // eslint-disable-next-line
  }, []);

  // Mark all as read in Supabase and locally
  const handleMarkAsRead = async () => {
    if (!userId) return;
    // Get all unread notification IDs
    const unreadIds = notifications.filter((n) => !n.isRead).map((n) => n.id);
    if (unreadIds.length === 0) return;

    // Batch update is_read for these IDs
    const { error } = await supabase
      .from("user_notifications")
      .update({ is_read: true })
      .in("id", unreadIds);

    if (error) {
      console.error("Error marking as read", error);
      toast.error("Failed to mark notifications as read.");
      return;
    }

    const newNotifs = notifications.map((n) =>
      unreadIds.includes(n.id) ? { ...n, isRead: true } : n
    );
    setNotifications(newNotifs);
    setUnreadCount(0);
  };

  // Mark single as read and remove from DB (delete)
  const handleDeleteNotification = async (id: string) => {
    // Mark as read first if needed
    const notif = notifications.find((n) => n.id === id);
    if (notif && !notif.isRead) {
      const { error: updateErr } = await supabase
        .from("user_notifications")
        .update({ is_read: true })
        .eq("id", id);
      if (updateErr) {
        console.error("Failed to mark as read", updateErr);
      }
    }

    // Delete notification from DB
    const { error } = await supabase
      .from("user_notifications")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Delete failed", error);
      toast.error("Failed to delete notification.");
      return;
    }

    // Update locally
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    setUnreadCount((prev) =>
      notif && !notif.isRead ? Math.max(0, prev - 1) : prev
    );
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
              {unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 p-0 bg-white dark:bg-gray-900 shadow-lg rounded-md border border-gray-200 dark:border-gray-700"
        align="end"
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Notifications</h3>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleMarkAsRead}
              className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Mark all as read
            </Button>
          )}
        </div>
        <div className="max-h-[300px] overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              No notifications
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-4 border-b last:border-b-0 border-gray-200 dark:border-gray-700 ${
                  !notification.isRead ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                } hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors`}
              >
                <div className="space-y-2">
                  <p className="text-sm text-gray-800 dark:text-gray-200">
                    {notification.message}
                  </p>
                  <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400">
                    <span>
                      {format(new Date(notification.timestamp), 'MMM dd, yyyy HH:mm')}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 p-0 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                      onClick={() => handleDeleteNotification(notification.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
